import React, { useMemo, useState } from 'react';
import { Grid, TextField, MenuItem } from '@material-ui/core';
import { OnwardCard } from './Card';
import { CardTitle, CardSubtitle, CardItem, Label } from './blocks';
import { css } from '@emotion/react';
import { startCase } from 'lodash';
import { genAttributes } from '@onward-delivery/core';
import { toNational } from '@/utilities/formatPhoneNumber';
import { toFullState } from '@/utilities/address';
import { format } from 'date-fns';
import { PrimaryButton } from '@/styles/blocks';
import useQuery from '@/utilities/useQuery';
import { post } from '@/utilities/onwardClient';
import { TRIGGER_ORDER_EVENT } from '@/constants/apiRoutes';
import { LOCATION_TYPES } from '@/constants/locationTypes';
import { MM_INTEGRATION_TYPES, E2E_INTEGRATION_TYPES } from '@/constants/integrationTypes';
import useAction from '@/utilities/useQuery';
import * as Sentry from '@sentry/react';
import { useClientUser } from '@/hooks';
import { carrierDisplayName } from '@/utilities/carrierAlias';

const dateShort = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});

const dollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

const MiddleMileCard = ({
    order,
    type,
    overridesById,
    isOnwardAdmin,
    isImposter,
    callbacks,
    loading,
    refetchOrder,
    setNotification,
    orderLoading,
}) => {
    // A split parent is frozen: hide the buttons that assign, cancel or re-send it to a partner.
    const isSplit = order?.order_status === 'split';
    const [mmOverride, setMMOverride] = useState(null);
    // Keyed by carrier_id, so it resolves the first-mile and middle-mile carriers alike —
    // both are clients the shipper may have relabelled.
    const { partnerCarriersById } = useClientUser();

    const [emit, { loading: emitLoading }] = useQuery(({ order_id, event }) => {
        return post(TRIGGER_ORDER_EVENT, { order_ids: [order_id], event });
    });

    const canRetry = useMemo(() => {
        return (
            (type === 'MIDDLE_MILE' &&
                (!order.middle_mile_integration_type || !order?.middle_mile_integration_meta?.tracking_number)) ||
            (type === 'E2E' && !order.external_integration_id)
        );
    }, [order, type]);

    const options = useMemo(() => {
        return (order.middle_mile_pricing_options || []).reduce((acc, breakdown) => {
            const override = overridesById[breakdown._meta.override_id];
            if (override && override.assign) {
                return [
                    ...acc,
                    {
                        id: breakdown._meta.override_id,
                        value: breakdown._meta.override_id,
                        display: override?.assign?.business_name,
                        price: dollarFormatter.format(breakdown.total_charge),
                    },
                ];
            }

            return acc;
        }, []);
    }, [order, overridesById]);

    const selected = useMemo(() => {
        if (options.length === 1) {
            return mmOverride ? mmOverride : options[0].id;
        }

        return mmOverride;
    }, [mmOverride, options]);

    const { phone, email, name } = useMemo(() => {
        return type === 'E2E'
            ? {
                  phone: 'pickup_phone',
                  email: 'pickup_email',
                  name: 'pickup_name',
              }
            : {
                  phone: 'middle_mile_origin_phone',
                  email: 'middle_mile_origin_email',
                  name: 'middle_mile_origin_name',
              };
    }, [type]);

    const { street, unit, city, state, zip, location, location_type } = genAttributes(
        order,
        type === 'MIDDLE_MILE'
            ? // always show mm facility addr even if it is techically not the origin for return mm leg.
              'ORIGIN'
            : 'PICKUP'
    );

    const [cancelMetroOrder, { loading: cancelingMetroOrder }] = useAction(
        async () =>
            post(TRIGGER_ORDER_EVENT, {
                order_ids: [order.order_id],
                event: 'mm_cancelled',
            }),
        {
            onComplete: (response) => {
                if (response?.request?.status === 200) {
                    setNotification({
                        severity: 'success',
                        message: 'Order cancelled successfully',
                    });
                    refetchOrder();
                } else {
                    Sentry.captureException(response?.data?.error || 'Error cancelling order');
                    setNotification({
                        severity: 'error',
                        message: response?.data?.error || 'Error cancelling order',
                    });
                }
            },
            onError: (err) => {
                console.error(err);
                Sentry.captureException(err);
                setNotification({
                    severity: 'error',
                    message: err?.response?.data?.error || 'Error cancelling order',
                });
            },
        }
    );

    return (
        <OnwardCard
            css={css`
                height: 100%;
            `}
        >
            <Grid container direction="column">
                <CardTitle>Middle Mile Origin Facility</CardTitle>

                {order.order_carrier && type === 'E2E' ? (
                    <>
                        <CardItem
                            css={css`
                                margin-bottom: 8px;
                            `}
                        >
                            {`Carrier Name: ${carrierDisplayName(order.order_carrier, partnerCarriersById) || '--'}`}
                        </CardItem>
                        <CardItem
                            css={css`
                                margin-bottom: 8px;
                            `}
                        >
                            {`Integration: ${E2E_INTEGRATION_TYPES[order.external_integration_attr] || '--'}`}
                        </CardItem>
                        <CardItem
                            css={css`
                                margin-bottom: 20px;
                            `}
                        >{`Tracking Number: ${order.external_integration_id || '--'}`}</CardItem>
                    </>
                ) : null}

                {type === 'MIDDLE_MILE' && !order.middle_mile_carrier_id && !isSplit ? (
                    <>
                        <CardItem
                            css={css`
                                margin-bottom: 16px;
                            `}
                        >
                            <CardSubtitle>Assign a middle mile carrier</CardSubtitle>
                        </CardItem>
                        <CardItem
                            css={css`
                                font-weight: 700;
                                margin-bottom: 8px;
                            `}
                        >
                            <TextField
                                select
                                fullWidth
                                label="Middle Mile Carrier"
                                variant="outlined"
                                value={selected}
                                onChange={(e) => setMMOverride(e.target.value)}
                            >
                                {options.map((option) => {
                                    return (
                                        <MenuItem
                                            key={option.id}
                                            value={option.value}
                                            css={css`
                                                justify-content: space-between;
                                            `}
                                        >
                                            <Grid
                                                direction="row"
                                                css={css`
                                                    width: 100%;
                                                    display: flex;
                                                `}
                                            >
                                                <Grid
                                                    direction="column"
                                                    css={css`
                                                        flex: 1;
                                                        flex-basis: 0;
                                                    `}
                                                >
                                                    <Label
                                                        css={css`
                                                            color: #2b2b2b;
                                                        `}
                                                    >
                                                        {option.display}
                                                    </Label>
                                                </Grid>
                                                <Grid
                                                    direction="column"
                                                    css={css`
                                                        flex: 0;
                                                        flex-basis: 0;
                                                    `}
                                                >
                                                    <Label
                                                        css={css`
                                                            color: #979797;
                                                        `}
                                                    >
                                                        {option.price}
                                                    </Label>
                                                </Grid>
                                            </Grid>
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        </CardItem>
                        <CardItem
                            css={css`
                                margin-bottom: 20px;
                            `}
                        >
                            <PrimaryButton
                                disabled={!selected || loading.update}
                                onClick={() => {
                                    callbacks.updateOrder({
                                        variables: {
                                            orders: [
                                                {
                                                    order_id: order.order_id,
                                                    middle_mile_shipper_rate_override_id: selected,
                                                },
                                            ],
                                            order_update_cols: ['middle_mile_shipper_rate_override_id'],
                                            items: [],
                                            removals: [],
                                            item_update_cols: [],
                                        },
                                    });
                                }}
                            >
                                Assign Carrier
                            </PrimaryButton>
                        </CardItem>
                    </>
                ) : null}

                {order.middle_mile_carrier_id && type === 'MIDDLE_MILE' ? (
                    <>
                        <CardItem
                            css={css`
                                font-weight: 700;
                                margin-bottom: 8px;
                            `}
                        >
                            {order.middle_mile_integration_status
                                ? startCase(order.middle_mile_integration_status.toLowerCase().replace('_', '-'))
                                : 'No Status Available'}
                            {!!order?.middle_mile_integration_meta?.last_modified_at
                                ? `: ${dateShort.format(
                                      new Date(order?.middle_mile_integration_meta?.last_modified_at)
                                  )}`
                                : null}
                        </CardItem>
                        <CardItem
                            css={css`
                                margin-bottom: 8px;
                            `}
                        >
                            {`Carrier Name: ${
                                carrierDisplayName(order.order_middle_mile_carrier, partnerCarriersById) || '--'
                            }`}
                        </CardItem>
                        <CardItem
                            css={css`
                                margin-bottom: 8px;
                            `}
                        >
                            {`Integration: ${
                                MM_INTEGRATION_TYPES[order.middle_mile_integration_type] ||
                                order.middle_mile_integration_type
                            }`}
                        </CardItem>
                        <CardItem
                            css={css`
                                margin-bottom: 20px;
                            `}
                        >
                            {`Tracking Number: ${order.middle_mile_integration_meta?.tracking_number || '--'}`}
                        </CardItem>
                    </>
                ) : null}

                {(isOnwardAdmin || isImposter) &&
                    !isSplit &&
                    order?.middle_mile_integration_type === 'METRO' &&
                    order?.middle_mile_integration_meta?.tracking_number && (
                        <>
                            <hr />
                            <CardItem
                                className="my-1"
                                css={css`
                                    margin-bottom: 20px;
                                `}
                            >
                                Onward Admins Only - Click the button below to cancel the order in Metro's platform.
                                This will also clear integration information from the order as well.
                            </CardItem>
                            <CardItem className="my-1">
                                Integration connection that will be deleted: {order?.middle_mile_integration_type}
                            </CardItem>
                            <PrimaryButton
                                variant="contained"
                                red={true}
                                style={{ width: 'fit-content' }}
                                disabled={cancelingMetroOrder || orderLoading}
                                onClick={() => {
                                    if (
                                        confirm(
                                            `Are you sure you want to cancel the order in Metro's platform and delete the order's integration information?`
                                        )
                                    ) {
                                        cancelMetroOrder();
                                    }
                                }}
                            >
                                Cancel Order in Metro's Platform
                            </PrimaryButton>
                            <hr />
                        </>
                    )}

                {(isOnwardAdmin || isImposter) && canRetry && !isSplit ? (
                    <>
                        <CardItem
                            css={css`
                                margin-bottom: 20px;
                            `}
                        >
                            <PrimaryButton
                                disabled={emitLoading}
                                onClick={() => {
                                    let event =
                                        type === 'E2E'
                                            ? 'carrier_assigned'
                                            : order.order_type === 'return'
                                            ? 'middle_mile_return_to_sender'
                                            : 'middle_mile_carrier_assigned';
                                    emit({
                                        order_id: order.order_id,
                                        event,
                                    });
                                }}
                            >
                                Retry Integration
                            </PrimaryButton>
                        </CardItem>
                    </>
                ) : null}

                <CardItem
                    css={css`
                        margin-bottom: 8px;
                    `}
                >
                    Origin Name: {order[name] || '--'}
                </CardItem>
                <CardItem
                    css={css`
                        margin-bottom: 8px;
                    `}
                >
                    Origin Email: {order[email] || '--'}
                </CardItem>
                <CardItem
                    css={css`
                        margin-bottom: 8px;
                    `}
                >
                    Origin Phone: {order[phone] ? toNational(order[phone]) : '--'}
                </CardItem>
                <CardItem
                    css={css`
                        margin-bottom: 8px;
                    `}
                >
                    {[
                        order[street],
                        ...(order[unit] ? [order[unit]] : []),
                        order[city],
                        toFullState(order[state]),
                        order[zip],
                    ].join(', ')}
                </CardItem>
                <CardItem
                    css={css`
                        margin-bottom: 8px;
                    `}
                >
                    {order[location] || ''} - {LOCATION_TYPES[order[location_type]] || ''}
                </CardItem>
            </Grid>
        </OnwardCard>
    );
};

export default MiddleMileCard;
